import { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { FeatureFlagServiceProps, withFeatureFlagService } from '@mews/framework';
import { TooltipContentElement, TooltipElement } from '@optimus-web/core/src/Tooltip/style';
import { TimeUnit } from '@mews/enums';
import { Resource } from '@mews-commander/resources';
import { FeatureFlag } from '@mews-commander/enums';
import { ApplicationState, WithServiceId } from '@mews-commander/types';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { canCurrentProfileManageReservations } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getCurrentCompanionAccommodationExtent } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { constructResourcesTree } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import {
    getService,
    getServiceEndOffset,
    getServiceStartOffset,
    getServiceTimeUnit,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { subscribeResourceUpdates, unsubscribeResourceUpdates } from '../../../core/websockets/eventHandlers/spaces';
import { subscribeReservationUpdates, unsubscribeReservationUpdates } from '../actions/subscriptions';
import Timeline from '../components/timeline';
import { MODULE_NAME, REFRESH_INTERVAL, TIMELINE_CANVAS_ID } from '../constants';
import {
    getCustomers,
    getSelectedResourceUse,
    getTimelineResourceUsesAndClustersOptimized,
    isPreviewDialogOpened as isPreviewDialogOpenedSelector,
} from '../reducers';
import {
    assignResource,
    deselectResourceUseWithRedirect,
    fetchBlock,
    fetchReservations,
    fetchTimelineResourceUses,
    resetTimeline,
    resourceDetail,
    selectResourceUseWithRedirect,
    timelineFilterSubmit,
    updateAssignedResourceLocked,
} from '../actions';
import { getEvents, getTimelineDraggingEnabled } from '../selector';
import { TimelineService } from '../timelineService';
import { getSelectedTimeUnit } from '../selectors/view';
import Model from '../components/model';
import { useTooltipText } from '../tooltipStore';
import { CalendarEventExtended, TimelineCanvas, TimelineOptions } from '../types';
import { TimelineFilterSchemaKeys, TimelineFilterValues } from '../components/TimelineFilters/timelineFilterSchema';
import { getMaximumAvailableEditableHistoryIntervalMilliseconds } from '../../newReservation/selectors';
import { NewReservationInfo } from '../../newReservation/components/NewReservationPanel/types';

const CanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;

interface OwnProps extends Pick<TimelineOptions, 'handleConfirmNewReservation'> {
    confirmedResourceUsePlaceholder: NewReservationInfo | null;
    newReservationPlaceholderLabel: string;
    handleCloseNewReservationPanel: () => void;
}

export interface Props extends FeatureFlagServiceProps, WithServiceId, OwnProps {
    handleAssignResource: $TSFixMeFunction;
    handleSelectResourceUse: $TSFixMeFunction;
    handleDeselectResourceUse: $TSFixMeFunction;
    handleFetchBlock: $TSFixMeFunction;
    handleResourceDetail: $TSFixMeFunction;
    handleFetchReservations: $TSFixMeFunction;
    fetchTimelineResourceUses: $TSFixMeFunction;
    resetTimeline: $TSFixMeFunction;
    subscribeReservationUpdates: $TSFixMeFunction;
    unsubscribeReservationUpdates: $TSFixMeFunction;
    subscribeResources: $TSFixMeFunction;
    unsubscribeResources: $TSFixMeFunction;
    isMobile: boolean;
    customers?: {};
    resourceUses?: {};
    filterValues: TimelineFilterValues;
    filterTimeUnit?: TimeUnit;
    spaces?: {};
    resourcesToHighlight: Resource[];
    events?: CalendarEventExtended[];
    selectedResourceUse?: {};
    canManageReservations?: boolean;
    timezone: string;
    isTimelineDraggingEnabled: boolean;
    timelineTouchScreenTest: boolean;
    ref?: React.Ref<HTMLElement>;
    selectedDensity: number | string;
    selectedDateTime?: string;
    selectedTimeUnit: TimeUnit;
    resources: ReturnType<typeof constructResourcesTree>;
    searchedSpace: Resource | null;
    hasMultipleServices: boolean;
    startOffset: string;
    endOffset: string;
    isBookable: boolean;
    serviceTimeUnit: TimeUnit;
    maximumAvailableEditableHistoryIntervalInMilliseconds: number;
    isPreviewDialogOpened: boolean;
}

class Canvas extends Component<Props> implements TimelineCanvas {
    // @ts-expect-error
    timeline: ReturnType<typeof Timeline>;

    refreshTimer: number | null = null;

    componentDidMount() {
        const {
            handleAssignResource,
            handleSelectResourceUse,
            handleDeselectResourceUse,
            handleConfirmNewReservation,
            // @ts-expect-error $TSFixMeError: Property 'handleChangeView' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; ...
            handleChangeView,
            handleResourceDetail,
            handleFetchReservations,
            handleFetchBlock,
            isMobile,
            canManageReservations,
            resetTimeline: onResetTimeline,
            subscribeReservationUpdates: onSubscribeReservationUpdates,
            subscribeResources,
            handleCloseNewReservationPanel,
            // @ts-expect-error $TSFixMeError: Property 'companionExtent' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            companionExtent,
            timelineTouchScreenTest,
            timezone,
            resourcesToHighlight,
            selectedDensity,
            selectedDateTime,
            selectedTimeUnit,
            featureFlagService,
            resources,
            filterValues,
            startOffset,
            endOffset,
            isBookable,
            serviceTimeUnit,
            maximumAvailableEditableHistoryIntervalInMilliseconds,
            isPreviewDialogOpened,
        } = this.props;

        const isPercentageDensityValuesEnabled = featureFlagService.hasFlag(FeatureFlag.percentageDensityValuesEnabled);
        const isTimelineTouchMoveEnabled = featureFlagService.hasFlag(FeatureFlag.timelineTouchMoveEnabled);
        const newReservationEnabled = isBookable && !isPreviewDialogOpened;
        onResetTimeline();
        onSubscribeReservationUpdates();
        subscribeResources();

        this.timeline = Timeline({
            target: MODULE_NAME,
            onViewportChanged: (
                startX: number,
                endX: number,
                startY: number,
                endY: number,
                model: ReturnType<typeof Model>
            ) => this.props.fetchTimelineResourceUses(startX, endX, startY, endY, model),
            refresh: (startX: number, endX: number, startY: number, endY: number, model: ReturnType<typeof Model>) =>
                this.props.fetchTimelineResourceUses(startX, endX, startY, endY, model, true),
            handleAssignResource,
            handleCloseNewReservationPanel,
            handleChangeView,
            handleDeselectResourceUse,
            handleSelectResourceUse,
            handleConfirmNewReservation,
            handleResourceDetail,
            handleFetchReservation: (id: $TSFixMe) => handleFetchReservations([id]),
            handleFetchBlock,
            isMobile,
            canManageReservations: !!canManageReservations,
            companionExtent,
            resourceToHighlight: resourcesToHighlight,
            serviceId: filterValues[TimelineFilterSchemaKeys.ServiceId],
            timelineTouchScreenTest,
            timezone,
            timeUnit: selectedTimeUnit,
            selectedDensity,
            selectedDateTime,
            isPercentageDensityValuesEnabled,
            isTimelineTouchMoveEnabled,
            newReservationEnabled,
            resources,
            startOffset,
            endOffset,
            serviceTimeUnit,
            maximumAvailableEditableHistoryIntervalInMilliseconds,
            confirmedResourceUsePlaceholder: this.props.confirmedResourceUsePlaceholder,
            newReservationPlaceholderLabel: this.props.newReservationPlaceholderLabel,
        });

        // To display correct index of clicked date legend unit on change view
        if (selectedDateTime && selectedTimeUnit) {
            const index = TimelineService.utcToTimeUnitIndex(selectedDateTime, timezone, selectedTimeUnit);
            this.timeline.goToIndex(index, true);
        }

        this.refreshTimer = window.setInterval(() => {
            this.timeline.refresh();
        }, REFRESH_INTERVAL);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // TODO: this is not shallow compare of props
        if (this.props !== nextProps) {
            const {
                customers,
                resourceUses,
                resources,
                resourcesToHighlight,
                events,
                selectedResourceUse,
                isMobile,
                selectedTimeUnit,
                isTimelineDraggingEnabled,
                selectedDateTime,
                selectedDensity,
                searchedSpace,
                filterValues,
                serviceTimeUnit,
                maximumAvailableEditableHistoryIntervalInMilliseconds,
                confirmedResourceUsePlaceholder,
                startOffset,
                endOffset,
                isBookable,
                isPreviewDialogOpened,
            } = nextProps;
            this.timeline.updateModel(
                customers,
                resourceUses,
                resources,
                resourcesToHighlight,
                events,
                selectedResourceUse,
                isMobile,
                isTimelineDraggingEnabled,
                filterValues,
                selectedDateTime,
                selectedDensity,
                selectedTimeUnit,
                searchedSpace,
                serviceTimeUnit,
                maximumAvailableEditableHistoryIntervalInMilliseconds,
                confirmedResourceUsePlaceholder,
                startOffset,
                endOffset,
                isBookable && !isPreviewDialogOpened
            );
        }
    }

    componentWillUnmount() {
        this.props.unsubscribeReservationUpdates();
        this.props.unsubscribeResources();
        this.timeline.dispose();
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
        }
    }

    handlePrevFast = () => this.timeline.prevFast(this.props.selectedTimeUnit);

    handlePrev = () => this.timeline.prev(this.props.selectedTimeUnit);

    handleToday = () => this.timeline.today();

    handleNext = () => this.timeline.next(this.props.selectedTimeUnit);

    handleNextFast = () => this.timeline.nextFast(this.props.selectedTimeUnit);

    handleGoToResource = (token: $TSFixMe) => this.timeline.goToResource(token);

    handleToggleResourceLegend = () => this.timeline.toggleResourceLegend();

    handleGoToDate = (utcDateString: string) => this.timeline.goToDate(utcDateString, this.props.selectedTimeUnit);

    handleGoToResourceUse = (id: $TSFixMe, isReservation: $TSFixMe) => this.timeline.findResourceUse(id, isReservation);

    clearConfirmedResourceUsePlaceholder = () => {
        this.timeline.clearConfirmedResourceUsePlaceholder();
    };

    render() {
        return (
            <CanvasWrapper id={MODULE_NAME}>
                <Tooltip />
                <canvas data-test-id={TIMELINE_CANVAS_ID} />
            </CanvasWrapper>
        );
    }
}

const Tooltip = () => {
    const text = useTooltipText();
    return (
        <TooltipElement className="tooltip" id="tooltip">
            <TooltipContentElement id="tooltip-text">{text}</TooltipContentElement>
        </TooltipElement>
    );
};

// @ts-expect-error
const selector = createStructuredSelector({
    resourceUses: getTimelineResourceUsesAndClustersOptimized,
    customers: getCustomers,
    selectedResourceUse: getSelectedResourceUse,
    events: getEvents,
    canManageReservations: canCurrentProfileManageReservations,
    companionExtent: getCurrentCompanionAccommodationExtent,
    selectedTimeUnit: getSelectedTimeUnit,
    timezone: getCurrentEnterpriseIanaTimeZoneIdentifier,
    isTimelineDraggingEnabled: getTimelineDraggingEnabled,
    startOffset: (state: ApplicationState, { serviceId }: WithServiceId) =>
        getServiceStartOffset(state, { id: serviceId }),
    endOffset: (state: ApplicationState, { serviceId }: WithServiceId) => getServiceEndOffset(state, { id: serviceId }),
    isBookable: (state: ApplicationState, { serviceId }: WithServiceId) => {
        const service = getService(state, { id: serviceId });
        return !!service && isBookableService(service);
    },
    serviceTimeUnit: (state: ApplicationState, { serviceId }: WithServiceId) =>
        getServiceTimeUnit(state, { id: serviceId }),
    maximumAvailableEditableHistoryIntervalInMilliseconds: getMaximumAvailableEditableHistoryIntervalMilliseconds,
    isPreviewDialogOpened: isPreviewDialogOpenedSelector,
});

export default connect(
    selector,
    {
        fetchTimelineResourceUses,
        resetTimeline,
        handleAssignResource: assignResource,
        handleSelectResourceUse: selectResourceUseWithRedirect,
        handleChangeView: timelineFilterSubmit,
        handleDeselectResourceUse: deselectResourceUseWithRedirect,
        handleFetchBlock: fetchBlock,
        handleResourceDetail: resourceDetail,
        handleFetchReservations: fetchReservations,
        updateAssignedResourceLocked,
        subscribeReservationUpdates,
        unsubscribeReservationUpdates,
        subscribeResources: subscribeResourceUpdates,
        unsubscribeResources: unsubscribeResourceUpdates,
    },
    null,
    { forwardRef: true }
    // @ts-expect-error $TSFixMeError: Argument of type 'ForwardRefExoticComponent<Props & RefAttributes<unknown>>' is not assignable to parameter of type 'Com...
)(withFeatureFlagService(Canvas));
